/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import { API_URL } from "../config";
import LoadingSpinner from "../components/LoadingSpinner.jsx";

// Helper function to parse single range string into min and max values
const parseNumericRange = (value) => {
  if (!value || typeof value !== "string") return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: rangeMatch[1], max: rangeMatch[2] };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: gteMatch[1], max: undefined };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: lteMatch[1] };
  }

  // Single number "100": set both min and max for exact match
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    return { min: singleMatch[1], max: singleMatch[1] };
  }

  return { min: undefined, max: undefined };
};

const OrderSettlementInfo = () => {
  const { id } = useParams();
  const [settlementData, setSettlementData] = useState(null);
  const [orderList, setOrderList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState("t1_created_at");
  const [sortOrder, setSortOrder] = useState("asc");

  // Date range state
  const [dateRange, setDateRange] = useState([null, null]);

  // Filtering state
  const initialFilters = {
    orderno: "",
    subscriber_name: "",
    bill_to: "",
    sid: "",
    utr_no: "",
    payid: "",
    type: "",
    credit_amount: "",
    actual_amount: "",
    fee: "",
    tax: "",
  };
  const [filters, setFilters] = useState(initialFilters);

  // Fetch data from API
  const fetchData = async (
    customFilters = filters,
    currentSortField = sortField,
    currentSortOrder = sortOrder,
    currentDateRange = dateRange
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      const params = {};

      if (currentDateRange && currentDateRange[0]) {
        params.from_date = moment(currentDateRange[0]).format("YYYY-MM-DD");
        params.to_date = currentDateRange[1]
          ? moment(currentDateRange[1]).format("YYYY-MM-DD")
          : params.from_date;
      }

      if (customFilters.orderno && customFilters.orderno.trim()) {
        params.orderno = customFilters.orderno.trim();
      }
      if (customFilters.subscriber_name && customFilters.subscriber_name.trim()) {
        params.subscriber_name = customFilters.subscriber_name.trim();
      }
      if (customFilters.bill_to && customFilters.bill_to.trim()) {
        params.bill_to = customFilters.bill_to.trim();
      }
      if (customFilters.sid && customFilters.sid.trim()) {
        params.sid = customFilters.sid.trim();
      }
      if (customFilters.utr_no && customFilters.utr_no.trim()) {
        params.utr_no = customFilters.utr_no.trim();
      }
      if (customFilters.payid && customFilters.payid.trim()) {
        params.payid = customFilters.payid.trim();
      }
      if (customFilters.type && customFilters.type.trim()) {
        params.type = customFilters.type.trim();
      }
      if (customFilters.credit_amount && customFilters.credit_amount.trim()) {
        params.credit_amount = customFilters.credit_amount.trim();
        const r = parseNumericRange(params.credit_amount);
        if (r.min !== undefined) params.min_credit_amount = r.min;
        if (r.max !== undefined) params.max_credit_amount = r.max;
      }
      if (customFilters.actual_amount && customFilters.actual_amount.trim()) {
        params.actual_amount = customFilters.actual_amount.trim();
        const r = parseNumericRange(params.actual_amount);
        if (r.min !== undefined) params.min_actual_amount = r.min;
        if (r.max !== undefined) params.max_actual_amount = r.max;
      }
      if (customFilters.fee && customFilters.fee.trim()) {
        params.fee = customFilters.fee.trim();
        const r = parseNumericRange(params.fee);
        if (r.min !== undefined) params.min_fee = r.min;
        if (r.max !== undefined) params.max_fee = r.max;
      }
      if (customFilters.tax && customFilters.tax.trim()) {
        params.tax = customFilters.tax.trim();
        const r = parseNumericRange(params.tax);
        if (r.min !== undefined) params.min_tax = r.min;
        if (r.max !== undefined) params.max_tax = r.max;
      }

      if (currentSortField) {
        params.orderBy = currentSortField;
        params.orderDir = currentSortOrder;
      }

      const response = await axios.get(
        `${API_URL}orderSettlement/details/${id}`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;
      setSettlementData(data);

      if (data && data.orderlist) {
        setOrderList(data.orderlist);
      } else {
        setOrderList([]);
      }
    } catch (error) {
      console.error("Error fetching settlement details:", error);
      setError("Failed to fetch settlement details");
      setSettlementData(null);
      setOrderList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Format date for display matching settlement list (DD-MM-YYYY HH:mm am/pm)
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  // Sort handler
  const sortBox = (field) => {
    const newOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newOrder);
    fetchData(filters, field, newOrder);
  };

  const getSortIcon = (column) => {
    if (sortField === column) {
      return sortOrder === "asc" ? (
        <FaSortUp style={{ color: "#242a30", marginLeft: "4px" }} />
      ) : (
        <FaSortDown style={{ color: "#242a30", marginLeft: "4px" }} />
      );
    }
    return <FaSort style={{ color: "#888", marginLeft: "4px" }} />;
  };

  // Check if any filter active
  const hasAnyFilter = useMemo(() => {
    if (dateRange[0] !== null) return true;
    return Object.entries(filters).some(([k, v]) => {
      if (v instanceof Date) return true;
      return v && String(v).trim() !== "";
    });
  }, [filters, dateRange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    const [start, end] = dates;
    if (start && end) {
      fetchData(filters, sortField, sortOrder, dates);
    } else if (!start && !end) {
      fetchData(filters, sortField, sortOrder, [null, null]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchData(filters, sortField, sortOrder, dateRange);
    }
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setFilters(initialFilters);
    fetchData(initialFilters, sortField, sortOrder, [null, null]);
  };

  // Recalculate totals on backend-filtered orderList
  const totals = useMemo(() => {
    return orderList.reduce(
      (acc, order) => {
        acc.creditAmount += parseFloat(order.t1_credit_amount || 0);
        acc.actualAmount += parseFloat(order.t1_actual_amount || 0);
        acc.feesAmount += parseFloat(order.t1_fee || 0);
        acc.taxAmount += parseFloat(order.t1_tax || 0);
        return acc;
      },
      { creditAmount: 0, actualAmount: 0, feesAmount: 0, taxAmount: 0 }
    );
  }, [orderList]);

  const typeOptions = useMemo(() => {
    const predefined = [
      { value: "payment", label: "Payment" },
      { value: "reversal", label: "Reversal" },
      { value: "refund", label: "Refund" },
      { value: "transfer", label: "Transfer" },
      { value: "adjustment", label: "Adjustment" },
    ];
    const map = new Map();
    predefined.forEach((opt) => map.set(opt.value.toLowerCase(), opt));
    orderList.forEach((order) => {
      if (order.t1_type && !map.has(order.t1_type.toLowerCase())) {
        const val = order.t1_type;
        const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
        map.set(val.toLowerCase(), { value: val, label: capitalized });
      }
    });
    return Array.from(map.values());
  }, [orderList]);

  return (
    <>
      <style>
        {`
          #doublescroll {
            max-height: 580px;
            overflow-y: auto;
            overflow-x: scroll;
            position: relative;
          }

          table.bg-white {
            min-height: 400px;
          }

          .table tbody tr {
            height: 50px;
          }

          .table th {
            vertical-align: middle !important;
          }

          .react-datepicker-wrapper {
            width: 100%;
          }

          .react-datepicker__input-container {
            width: 100%;
          }

          .react-datepicker__input-container input {
            width: 100% !important;
          }

          .table-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255,255,255,0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            border-radius: 8px;
          }
        `}
      </style>

      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content">
          {error && <div className="alert alert-danger">{error}</div>}

          {/* Header Row */}
          <div className="row pb-2">
            <div className="col-md-12" style={{ textAlign: "right" }}>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <span style={{ fontSize: "0.8rem" }}>
                  Showing {orderList.length} records
                </span>
                <button
                  className="btn btn-default btn-sm"
                  onClick={() => fetchData(filters, sortField, sortOrder)}
                  disabled={isLoading}
                  title="Refresh"
                >
                  <FiRefreshCw />
                </button>

                {hasAnyFilter && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleClearFilters}
                    title="Clear All Filters"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table Container with exact same panel & styling as settlement page */}
          <div
            className="panel panel-default with-rounded-corner tbl_head_sticky tbl_col_sticky with-shadow p-10"
            id="doublescroll"
          >
            {isLoading && (
              <div className="table-overlay">
                <LoadingSpinner />
              </div>
            )}

            <table className="table table-condensed table-hover bg-white">
              <thead>
                {/* 1. Header & Sort Row */}
                <tr>
                  <th
                    style={{ minWidth: "150px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_created_at")}
                  >
                    Created {getSortIcon("t1_created_at")}
                  </th>
                  <th
                    style={{ minWidth: "120px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("orderno")}
                  >
                    Order No. {getSortIcon("orderno")}
                  </th>
                  <th
                    style={{ minWidth: "180px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("subscriber_name")}
                  >
                    Subscriber Name {getSortIcon("subscriber_name")}
                  </th>
                  <th
                    style={{ minWidth: "180px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("bill_to")}
                  >
                    Bill To {getSortIcon("bill_to")}
                  </th>
                  <th
                    style={{ minWidth: "160px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("sid")}
                  >
                    Settlement ID {getSortIcon("sid")}
                  </th>
                  <th
                    style={{ minWidth: "140px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("utr_no")}
                  >
                    UTR No {getSortIcon("utr_no")}
                  </th>
                  <th
                    style={{ minWidth: "160px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_payid")}
                  >
                    Payment ID {getSortIcon("t1_payid")}
                  </th>
                  <th
                    style={{ minWidth: "100px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_type")}
                  >
                    Type {getSortIcon("t1_type")}
                  </th>
                  <th
                    style={{ minWidth: "135px", textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_credit_amount")}
                  >
                    Credit Amount {getSortIcon("t1_credit_amount")}
                  </th>
                  <th
                    style={{ minWidth: "135px", textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_actual_amount")}
                  >
                    Actual Amount {getSortIcon("t1_actual_amount")}
                  </th>
                  <th
                    style={{ minWidth: "115px", textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_fee")}
                  >
                    Fees {getSortIcon("t1_fee")}
                  </th>
                  <th
                    style={{ minWidth: "110px", textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("t1_tax")}
                  >
                    Tax {getSortIcon("t1_tax")}
                  </th>
                </tr>

                {/* 2. Column Filter Row */}
                <tr>
                  <th style={{ minWidth: "210px" }}>
                    <DatePicker
                      selectsRange={true}
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      onChange={handleDateChange}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="Filter date range"
                      isClearable
                      monthsShown={2}
                      openToDate={new Date()}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="orderno"
                      className="form-control"
                      placeholder="Filter Order"
                      value={filters.orderno}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="subscriber_name"
                      className="form-control"
                      placeholder="Filter Subscriber Name"
                      value={filters.subscriber_name}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="bill_to"
                      className="form-control"
                      placeholder="Filter Bill To"
                      value={filters.bill_to}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="sid"
                      className="form-control"
                      placeholder="Filter Settlement ID"
                      value={filters.sid}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="utr_no"
                      className="form-control"
                      placeholder="Filter UTR No"
                      value={filters.utr_no}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="payid"
                      className="form-control"
                      placeholder="Filter Payment ID"
                      value={filters.payid}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <select
                      name="type"
                      className="form-select form-control"
                      value={filters.type || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        const updatedFilters = { ...filters, type: val };
                        setFilters(updatedFilters);
                        fetchData(updatedFilters, sortField, sortOrder, dateRange);
                      }}
                      disabled={isLoading}
                    >
                      <option value="">All Types</option>
                      {typeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <input
                      type="text"
                      name="credit_amount"
                      className="form-control text-end"
                      placeholder="e.g. 100-5000"
                      title="Enter single value or range (e.g. 100-5000, >=100, <=5000)"
                      value={filters.credit_amount}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="actual_amount"
                      className="form-control text-end"
                      placeholder="e.g. 100-5000"
                      title="Enter single value or range (e.g. 100-5000, >=100, <=5000)"
                      value={filters.actual_amount}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="fee"
                      className="form-control text-end"
                      placeholder="e.g. 10-500"
                      title="Enter single value or range (e.g. 10-500, >=10, <=500)"
                      value={filters.fee}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="tax"
                      className="form-control text-end"
                      placeholder="e.g. 1-100"
                      title="Enter single value or range (e.g. 1-100, >=1, <=100)"
                      value={filters.tax}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                    />
                  </th>
                </tr>
              </thead>

              <tbody>
                {orderList.length > 0 ? (
                  orderList.map((order, index) => (
                    <tr key={order.t1_id || index}>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(order.t1_created_at)}</td>
                      <td>{order.orderno || "-"}</td>
                      <td>{order.subscriber_name || "-"}</td>
                      <td>{order.bill_to || "-"}</td>
                      <td>{order.t1_sid || order.sid || "-"}</td>
                      <td>{order.utr_no || "-"}</td>
                      <td>{order.t1_payid || "-"}</td>
                      <td>{order.t1_type || "-"}</td>
                      <td style={{ textAlign: "right" }}>{formatCurrency(order.t1_credit_amount)}</td>
                      <td style={{ textAlign: "right" }}>{formatCurrency(order.t1_actual_amount)}</td>
                      <td style={{ textAlign: "right" }}>{formatCurrency(order.t1_fee)}</td>
                      <td style={{ textAlign: "right" }}>{formatCurrency(order.t1_tax)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center fw-bold py-4">
                      {isLoading ? "Loading..." : "No Records Found"}
                    </td>
                  </tr>
                )}
              </tbody>

              {orderList.length > 0 && (
                <tfoot className="table-secondary">
                  <tr>
                    <td colSpan="8" className="text-start fw-bold">
                      Showing {orderList.length} records
                    </td>
                    <td className="text-end" style={{ fontWeight: "bold" }}>
                      {formatCurrency(totals.creditAmount)}
                    </td>
                    <td className="text-end" style={{ fontWeight: "bold" }}>
                      {formatCurrency(totals.actualAmount)}
                    </td>
                    <td className="text-end" style={{ fontWeight: "bold" }}>
                      {formatCurrency(totals.feesAmount)}
                    </td>
                    <td className="text-end" style={{ fontWeight: "bold" }}>
                      {formatCurrency(totals.taxAmount)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSettlementInfo;
